import React, { useEffect, useState } from "react";
import { PATHS } from "../../config/permissionsPaths";
import useAuth from "../../hooks/useAuth";
import Modal from "./Modal";
import { BtnGeneral } from "../../styles/Buttons";
import { useLocation } from "../../hooks/useLocation";
import { toast } from "react-toastify";
import { getAddresses, saveAddress, deactivateAddress } from "../../api/addressApi";
import { usePatientData } from "../../context/PatientDataContext";
import icons from "../../assets/icons/iconMap";

const AddressModal = ({ isOpen, onClose, id }) => {
  const [selectedDept, setSelectedDept] = useState("");
  const [selectedTown, setSelectedTown] = useState("");
  const [neighborhood, setNeighborhood] = useState("");
  const [address, setAddress] = useState("");
  const [isPrincipal, setIsPrincipal] = useState(false);
  const [addresses, setAddresses] = useState([]);
  const [editingAddress, setEditingAddress] = useState(null);
  const [pendingTownId, setPendingTownId] = useState(null);
  const [saving, setSaving] = useState(false);

  // Campos SNR
  const [tipoVia, setTipoVia] = useState("");
  const [viaPrincipal, setViaPrincipal] = useState("");
  const [viaGeneradora, setViaGeneradora] = useState("");
  const [numeroPredio, setNumeroPredio] = useState("");
  const [complemento, setComplemento] = useState("");

  const { departments, towns } = useLocation(selectedDept, isOpen);
  const { hasPermission } = useAuth();

  const { fetchPatientData } = usePatientData();

  /** Cargar direcciones */
  const loadAddresses = async () => {
    if (!id) return;
    const data = await getAddresses(id);
    setAddresses(data);
  };

  useEffect(() => {
    if (isOpen && id) {
      loadAddresses();
      resetForm();
    }
  }, [isOpen, id]);

  /** Setear municipio cuando los towns se carguen (para edición) */
  useEffect(() => {
    
    if (pendingTownId && towns.length > 0) {
      // Verificar que el municipio exista en la lista antes de setearlo
      const townExists = towns.some(t => String(t.codigo ?? "").trim().padStart(3, "0") === String(pendingTownId).trim().padStart(3, "0"));
      console.log("¿Municipio existe?", townExists);
      
      if (townExists) {
        console.log("Seteando municipio:", pendingTownId);
        setSelectedTown(pendingTownId);
        setPendingTownId(null);
      } else {
        console.error("Municipio no encontrado en la lista:", pendingTownId);
      }
    }
  }, [towns]);

  /** Autogenerar cadena SNR */
  useEffect(() => {
    if (!tipoVia && !viaPrincipal && !viaGeneradora && !numeroPredio && !complemento) return;

    const mainPart = [tipoVia, viaPrincipal].filter(Boolean).join(" ");
    const placa =
      viaGeneradora && numeroPredio
        ? `# ${viaGeneradora}-${numeroPredio}`
        : viaGeneradora
        ? `# ${viaGeneradora}`
        : numeroPredio
        ? `# ${numeroPredio}`
        : "";

    const formatted = `${mainPart} ${placa} ${complemento}`.replace(/\s+/g, " ").trim();
    setAddress(formatted);
  }, [tipoVia, viaPrincipal, viaGeneradora, numeroPredio, complemento]);

  /** Resetear formulario */
  const resetForm = () => {
    setNeighborhood("");
    setAddress("");
    setSelectedDept("");
    setSelectedTown("");
    setIsPrincipal(false);
    setTipoVia("");
    setViaPrincipal("");
    setViaGeneradora("");
    setNumeroPredio("");
    setComplemento("");
    setEditingAddress(null);
    setPendingTownId(null);
  };

  /** Guardar dirección */
  const addAddress = async () => {
    if (saving) return;
    if (!selectedDept || !selectedTown || !neighborhood || !address) {
      toast.error("Todos los campos son obligatorios");
      return;
    }

    try {
      setSaving(true);
      const rs = await saveAddress({
        autoid: id,
        departamento: selectedDept,
        municipio: selectedTown,
        barrio: neighborhood,
        direccion: address,
        principal: isPrincipal ? "1" : "0",
      });

      if (!rs) return;

      resetForm();
      await loadAddresses();
      fetchPatientData();
      toast.success("Dirección guardada correctamente");
      onClose();
    } catch (err) {
      console.error("Error al guardar dirección:", err);
    } finally {
      setSaving(false);
    }
  };

  /** Actualizar dirección */
  const updateAddress = async () => {
    if (saving) return;
    if (!selectedDept || !selectedTown || !neighborhood || !address) {
      toast.error("Todos los campos son obligatorios");
      return;
    }

    try {
      setSaving(true);
      const rs = await saveAddress({
        id: editingAddress.id,
        autoid: id,
        departamento: selectedDept,
        municipio: selectedTown,
        barrio: neighborhood,
        direccion: address,
        principal: isPrincipal ? "1" : "0",
      });

      if (!rs) return;

      resetForm();
      await loadAddresses();
      fetchPatientData();
      toast.success("Dirección actualizada correctamente");
    } catch (err) {
      console.error("Error al actualizar dirección:", err);
    } finally {
      setSaving(false);
    }
  };

  /** Cargar datos para editar */
  const handleEditAddress = (addr) => {
    console.log("Editando dirección:", addr);
    console.log("idDep:", addr.idDep, "codMuni:", addr.codMuni);
    
    setEditingAddress(addr);
    setSelectedDept(addr.idDep ? String(addr.idDep).trim().padStart(2, "0") : ""); // Usar idDep del objeto
    setPendingTownId(addr.codMuni ? String(addr.codMuni).trim().padStart(3, "0") : ""); // Usar codMuni para setearlo después
    setNeighborhood(addr.barrio);
    setAddress(addr.direccion);
    setIsPrincipal(addr.principal === "1");

    // Limpiar campos SNR al editar (ya que la dirección ya está formateada)
    setTipoVia("");
    setViaPrincipal("");
    setViaGeneradora("");
    setNumeroPredio("");
    setComplemento("");
  };

  /** Inactivar dirección */
  const removeAddress = async (addrId) => {
    const ok = await deactivateAddress(addrId);
    if (ok) {
      await loadAddresses();
      fetchPatientData();
      toast.success("Dirección eliminada correctamente");
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Direcciones del Paciente" width="700px">
      {hasPermission(PATHS.CREAR_DIRECCION) && (
        <>
          {/* FORM */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "7px",
              marginBottom: "10px",
            }}
          >
            <select
              value={selectedDept}
              onChange={(e) => setSelectedDept(e.target.value)}
              style={{ padding: "6px", borderRadius: "6px" }}
            >
              <option value="">Seleccione departamento</option>
              {departments.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.descripcion}
                </option>
              ))}
            </select>

            <select
              value={selectedTown}
              onChange={(e) => setSelectedTown(e.target.value)}
              disabled={!selectedDept}
              style={{ padding: "6px", borderRadius: "6px" }}
            >
              <option value="">Seleccione municipio</option>
              {towns.map((t) => (
                <option key={t.codigo} value={t.codigo}>
                  {t.descripcion}
                </option>
              ))}
            </select>

            <input
              type="text"
              value={neighborhood}
              onChange={(e) => setNeighborhood(e.target.value)}
              placeholder="Barrio"
              style={{ padding: "6px", borderRadius: "6px" }}
            />

            <input
              type="text"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="Dirección"
              style={{ padding: "6px", borderRadius: "6px" }}
            />

            <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
              <label>Es Principal</label>
              <input
                type="checkbox"
                checked={isPrincipal}
                onChange={(e) => setIsPrincipal(e.target.checked)}
              />
            </div>

            {/* BLOQUE SNR - Solo mostrar si NO está editando */}
            {!editingAddress && (
              <div
                style={{
                  gridColumn: "span 2",
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 1fr)",
                  gap: "7px",
                  marginTop: "6px",
                }}
              >
                <select value={tipoVia} onChange={(e) => setTipoVia(e.target.value)}>
                  <option value="">Tipo de vía</option>
                  <option value="CL">Calle</option>
                  <option value="KR">Carrera</option>
                  <option value="AC">Avenida Calle</option>
                  <option value="AK">Avenida Carrera</option>
                  <option value="DG">Diagonal</option>
                  <option value="TV">Transversal</option>
                </select>

                <input
                  type="text"
                  value={viaPrincipal}
                  onChange={(e) => setViaPrincipal(e.target.value)}
                  placeholder="Vía Principal"
                />

                <input
                  type="text"
                  value={viaGeneradora}
                  onChange={(e) => setViaGeneradora(e.target.value)}
                  placeholder="Vía Generadora"
                />

                <input
                  type="text"
                  value={numeroPredio}
                  onChange={(e) => setNumeroPredio(e.target.value)}
                  placeholder="Placa / Nº Predio"
                />

                <input
                  type="text"
                  value={complemento}
                  onChange={(e) => setComplemento(e.target.value)}
                  placeholder="Apto, Torre..."
                />
              </div>
            )}
          </div>

          <div style={{ display: "flex", gap: "10px" }}>
            <BtnGeneral
              bg={editingAddress ? "blue" : "green"}
              padding="7px"
              radius="8px"
              w="80px"
              disabled={saving}
              onClick={editingAddress ? updateAddress : addAddress}
            >
              {saving ? "Guardando..." : editingAddress ? "Actualizar" : "Guardar"}
            </BtnGeneral>

            {editingAddress && (
              <BtnGeneral bg="gray" padding="7px" radius="8px" w="80px" disabled={saving} onClick={resetForm}>
                Cancelar
              </BtnGeneral>
            )}
          </div>

          <hr style={{ margin: "5px 0" }} />
        </>
      )}

      {/* LISTA */}
      {addresses.length > 0 ? (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ backgroundColor: "#f0f0f0" }}>
                <th style={thStyle}>Departamento</th>
                <th style={thStyle}>Municipio</th>
                <th style={thStyle}>Barrio</th>
                <th style={thStyle}>Dirección</th>
                <th style={thStyle}>Principal</th>
                {(hasPermission(PATHS.CREAR_DIRECCION) || hasPermission(PATHS.ELIMINAR_DIRECCION)) && (
                  <th style={thStyle}>Acciones</th>
                )}
              </tr>
            </thead>
            <tbody>
              {addresses.map((a, i) => (
                <tr
                  key={i}
                  style={{
                    backgroundColor: editingAddress?.id === a.id ? "#e3f2fd" : "transparent",
                  }}
                >
                  <td style={tdStyle}>{a.departamento}</td>
                  <td style={tdStyle}>{a.municipio}</td>
                  <td style={tdStyle}>{a.barrio}</td>
                  <td style={tdStyle}>{a.direccion}</td>
                  <td style={tdStyle}>{a.principal === "1" ? "SI" : "NO"}</td>
                  {(hasPermission(PATHS.CREAR_DIRECCION) || hasPermission(PATHS.ELIMINAR_DIRECCION)) && (
                    <td style={tdStyle}>
                      <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
                        {hasPermission(PATHS.CREAR_DIRECCION) && (

                          <img
                            src={icons["pencil"]}
                            alt="Editar Direccion"
                            title="Editar Direccion"
                            style={{
                              border: "none",
                              background: "transparent",
                              cursor: "pointer",
                              width: 16,
                              height: 16,
                            }}
                            className="hover"
                            onClick={() => handleEditAddress(a)}
                          />
                        )}
                        {hasPermission(PATHS.ELIMINAR_DIRECCION) && (
                          <button
                            onClick={() => removeAddress(a.id)}
                            style={{
                              border: "none",
                              background: "transparent",
                              cursor: "pointer",
                              fontSize: "16px",
                              padding: 0,
                              lineHeight: 1,
                            }}
                            className="hover"
                            title="Eliminar"
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p>No hay direcciones registradas.</p>
      )}
    </Modal>
  );
};

const thStyle = {
  padding: "8px",
  borderBottom: "2px solid #ccc",
  fontWeight: "600",
  fontSize: "14px",
};

const tdStyle = {
  padding: "8px",
  borderBottom: "1px solid #ddd",
  fontSize: "13px",
};

export default AddressModal;
 